import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CircleNotch, FloppyDisk, IdentificationCard, ImageSquare } from "@phosphor-icons/react";
import { LanguageMultiCombobox } from "@/components/ui/language-multi-combobox";
import { Button } from "@/components/ui/button";
import { Img } from "@/components/ui/img";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiSend } from "@/lib/api-error";
import { stripBosnian } from "@/lib/languages";
import { toast } from "@/lib/toast";
import { uploadFile } from "@/lib/upload";
import { cn } from "@/lib/utils";
import { SectionShell, SettingRow } from "./section-primitives";
import { THERAPIST_BIO_MAX_LENGTH as BIO_MAX_LENGTH, updateTherapistSchema } from "@/schemas/therapist";
import { isValidPhone } from "@/lib/phone";
import { formatValidationError } from "@/lib/validation-errors";
import type { ProfileWithMeta } from "./types";

export function ProfileSection({ profile }: { profile: ProfileWithMeta }) {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState(profile.userName || "");
  const [bio, setBio] = useState(profile.bio || "");
  const [phone, setPhone] = useState(profile.phone || "");
  const [licenseNumber, setLicenseNumber] = useState(profile.licenseNumber || "");
  const [sessionModality, setSessionModality] = useState(profile.sessionModality ?? "online");
  const [officeAddress, setOfficeAddress] = useState(profile.officeAddress ?? "");
  const [languages, setLanguages] = useState(stripBosnian(profile.languages ?? []));
  const [photoUrl, setPhotoUrl] = useState<string | null>(profile.profileImageUrl);
  const [videoUrl, setVideoUrl] = useState<string | null>(profile.profileVideoUrl);
  const [specializations, setSpecializations] = useState<string[]>(() => {
    try {
      const parsed = JSON.parse(profile.specializations || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const formValues = () => ({
    fullName: fullName.trim(),
    bio,
    specializations,
    phone: phone.trim(),
    licenseNumber,
    sessionModality,
    officeAddress: officeAddress.trim(),
    languages,
    // Fotografija je obavezna na profilu, pa se može samo zamijeniti, ne obrisati.
    profileImageUrl: photoUrl || undefined,
    profileVideoUrl: videoUrl || null,
  });
  // Šalju se samo izmijenjena polja: polje koje terapeut nije dirao (npr. prazan
  // telefon ili kratak bio iz WP migracije) ne smije srušiti spremanje ostalih.
  const savedValues = useRef<ReturnType<typeof formValues> | null>(null);
  if (savedValues.current === null) savedValues.current = formValues();

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError(null);
    setIsUploadingPhoto(true);
    try {
      const result = await uploadFile(file, "profiles");
      setPhotoUrl(result.fileUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Greška pri uploadu.";
      setPhotoError(msg);
      toast.error(msg);
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = "";
    }
  }

  const specsQuery = useQuery({
    queryKey: ["specializations"],
    queryFn: () =>
      apiGet<{ data: { id: string; name: string; iconUrl: string | null }[] }>(
        "/api/specializations",
        "Greška pri učitavanju specijalizacija",
      ),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiSend(`/api/therapists/${profile.id}`, "PATCH", data, "Greška pri spremanju profila"),
    onSuccess: (_res, data) => {
      savedValues.current = { ...savedValues.current!, ...data };
      queryClient.invalidateQueries({ queryKey: ["therapist", "profile", "me"] });
      toast.success("Profil je sačuvan.");
    },
  });

  const save = () => {
    if (bio.length > BIO_MAX_LENGTH) {
      toast.error(`Bio je predug: dozvoljeno je ${BIO_MAX_LENGTH} znakova, a uneseno je ${bio.length}. Skratite tekst pa sačuvajte.`);
      return;
    }
    const current = formValues();
    const saved = savedValues.current!;
    const payload = Object.fromEntries(
      Object.entries(current).filter(
        ([key, value]) => JSON.stringify(value) !== JSON.stringify(saved[key as keyof typeof saved]),
      ),
    );
    if (Object.keys(payload).length === 0) {
      toast.success("Nema izmjena za spremanje.");
      return;
    }
    // Ista shema kao na serveru, da se greška vidi prije slanja i istim riječima.
    const parsed = updateTherapistSchema.safeParse(payload);
    if (!parsed.success) {
      toast.error(formatValidationError(parsed.error.issues, payload).error);
      return;
    }
    updateMutation.mutate(payload);
  };

  const toggleSpec = (name: string) => {
    setSpecializations((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
  };

  return (
    <SectionShell
      icon={IdentificationCard}
      title="Javni profil"
      description="Podaci koje klijenti vide kada procjenjuju vaš pristup, stručnost i način rada."
      footer={
        <Button onClick={save} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
          Sačuvaj profil
        </Button>
      }
    >
      <input
        type="file"
        id="settings-profile-photo"
        accept="image/jpeg,image/png,image/webp"
        onChange={handlePhotoUpload}
        className="hidden"
      />

      <SettingRow label="Fotografija" description="JPG, PNG ili WebP · maks. 5 MB">
        <div className="flex items-center gap-4 sm:justify-end">
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-3 text-sm">
              <Label
                htmlFor="settings-profile-photo"
                className="cursor-pointer font-medium text-primary hover:underline"
              >
                {photoUrl ? "Promijeni" : "Dodaj"}
              </Label>
            </div>
            {photoError && <p className="text-xs text-rose-600">{photoError}</p>}
          </div>
          <Label
            htmlFor="settings-profile-photo"
            className={cn(
              "relative grid size-11 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-full border border-border/60 bg-muted/40 transition-colors",
              !photoUrl && !isUploadingPhoto && "hover:border-primary/40 hover:bg-primary/5",
            )}
          >
            {isUploadingPhoto ? (
              <CircleNotch className="size-5 animate-spin text-primary/50" />
            ) : photoUrl ? (
              <Img src={photoUrl} alt={fullName || "Profil"} className="size-full object-cover" />
            ) : (
              <ImageSquare className="size-5 text-muted-foreground/40" />
            )}
          </Label>
        </div>
      </SettingRow>

      <SettingRow label="Ime i prezime" htmlFor="settings-fullname">
        <Input id="settings-fullname" value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </SettingRow>

      <SettingRow label="Telefon" htmlFor="settings-phone">
        <Input
          id="settings-phone"
          type="tel"
          placeholder="+387 61 234 567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          aria-invalid={phone.trim() !== "" && !isValidPhone(phone)}
        />
      </SettingRow>

      <SettingRow label="Bio" description="Kratak opis vašeg pristupa radu." htmlFor="settings-bio" block>
        <Textarea
          id="settings-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          className="min-h-24"
          aria-invalid={bio.length > BIO_MAX_LENGTH}
        />
        <p
          className={cn(
            "mt-1.5 text-right text-xs tabular-nums",
            bio.length > BIO_MAX_LENGTH ? "font-medium text-destructive" : "text-muted-foreground",
          )}
        >
          {bio.length > BIO_MAX_LENGTH
            ? `Predugo za ${bio.length - BIO_MAX_LENGTH} znakova (${bio.length}/${BIO_MAX_LENGTH})`
            : `${bio.length}/${BIO_MAX_LENGTH}`}
        </p>
      </SettingRow>

      <SettingRow label="Broj licence" htmlFor="settings-license">
        <Input id="settings-license" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
      </SettingRow>

      <SettingRow label="Specijalizacije" description="Odaberite oblasti u kojima radite." block>
        <div className="flex flex-wrap gap-2">
          {(specsQuery.data?.data ?? []).map((spec) => {
            const checked = specializations.includes(spec.name);
            return (
              <button
                key={spec.id}
                type="button"
                onClick={() => toggleSpec(spec.name)}
                aria-pressed={checked}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors",
                  checked
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                )}
              >
                {checked ? (
                  <Check className="size-4 shrink-0" weight="bold" />
                ) : spec.iconUrl ? (
                  <img src={spec.iconUrl} alt="" className="size-4 shrink-0 rounded object-cover" />
                ) : null}
                {spec.name}
              </button>
            );
          })}
        </div>
      </SettingRow>

      <SettingRow label="Način rada" block>
        <div className="grid gap-2.5 sm:grid-cols-3">
          {[
            { value: "online", label: "Samo online" },
            { value: "in_person", label: "Samo uživo" },
            { value: "both", label: "Online i uživo" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSessionModality(opt.value as typeof sessionModality)}
              className={cn(
                "rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors",
                sessionModality === opt.value
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </SettingRow>

      <SettingRow label="Adresa ordinacije" htmlFor="settings-office">
        <Input
          id="settings-office"
          value={officeAddress}
          onChange={(e) => setOfficeAddress(e.target.value)}
          placeholder="npr. Titova 12, Sarajevo"
        />
      </SettingRow>

      <SettingRow label="Dodatni jezici" block>
        <LanguageMultiCombobox value={languages} onChange={setLanguages} />
      </SettingRow>

      <SettingRow label="Intro video" description="YouTube ili Vimeo link" htmlFor="settings-video">
        <Input
          id="settings-video"
          type="url"
          value={videoUrl ?? ""}
          onChange={(e) => setVideoUrl(e.target.value.trim() || null)}
          placeholder="https://youtube.com/watch?v=..."
        />
      </SettingRow>

      <SettingRow label="Certifikati i dokumenti" block>
        {(profile.documents ?? []).length === 0 ? (
          <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
            Nema dokumenata.
          </p>
        ) : (
          <div className="divide-y divide-border/50 rounded-lg border border-border/60">
            {(profile.documents ?? []).map((doc) => (
              <a
                key={doc.id}
                href={doc.fileUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-muted/40"
              >
                <span className="font-medium text-foreground">{doc.name}</span>
                <span className="text-xs text-muted-foreground">{doc.fileType}</span>
              </a>
            ))}
          </div>
        )}
      </SettingRow>
    </SectionShell>
  );
}
